/**
 * Geolocation utility — derives a postcode (US ZIP, UK postcode, CA postal,
 * or international equivalent) from device GPS coordinates.
 *
 * Uses the browser Geolocation API + BigDataCloud free reverse-geocoding
 * (no API key required). Coordinates are never stored — only the derived
 * postcode is returned to the caller.
 */

const GEOCODE_URL = 'https://api.bigdatacloud.net/data/reverse-geocode-client';
const POSITION_TIMEOUT = 10000; // 10 s

/**
 * Check whether the browser supports geolocation.
 * @returns {boolean}
 */
export function isGeolocationSupported() {
  return typeof navigator !== 'undefined' && 'geolocation' in navigator;
}

/**
 * Request device location and reverse-geocode it to a postcode.
 * Returns a US ZIP, UK postcode, CA postal, or international equivalent
 * — whatever BigDataCloud's `postcode` field contains for that location.
 *
 * @returns {Promise<{ zip: string|null, error: string|null }>}
 */
export async function getZipFromLocation() {
  if (!isGeolocationSupported()) {
    return { zip: null, error: 'Location services not available on this device.' };
  }

  // 1 — Get GPS coordinates
  let coords;
  try {
    coords = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos.coords),
        (err) => reject(err),
        { enableHighAccuracy: false, timeout: POSITION_TIMEOUT, maximumAge: 60000 },
      );
    });
  } catch (err) {
    if (import.meta.env.DEV) console.error('[geolocation] position error', err);
    switch (err?.code) {
      case 1: // PERMISSION_DENIED
        return { zip: null, error: 'Location access denied. You can enter your postcode manually.' };
      case 2: // POSITION_UNAVAILABLE
        return { zip: null, error: 'Unable to determine location. Please enter your postcode manually.' };
      case 3: // TIMEOUT
        return { zip: null, error: 'Location request timed out. Please enter your postcode manually.' };
      default:
        return { zip: null, error: 'Could not get location. Please enter your postcode manually.' };
    }
  }

  // 2 — Reverse-geocode to a postcode (US ZIP, UK postcode, etc.)
  try {
    const url = `${GEOCODE_URL}?latitude=${coords.latitude}&longitude=${coords.longitude}&localityLanguage=en`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Geocode HTTP ${res.status}`);
    const data = await res.json();

    const raw = (data.postcode || '').toString().trim();
    if (!raw) {
      return { zip: null, error: 'Could not determine postcode from your location.' };
    }

    const country = (data.countryCode || '').toUpperCase();
    let zip;
    if (country === 'US') {
      // US ZIP: take first 5 digits (handles ZIP+4)
      const digits = raw.replace(/\D/g, '').slice(0, 5);
      zip = digits.length === 5 ? digits : null;
    } else {
      // Everywhere else: keep BigDataCloud's postcode as-is, capped at 10
      // alphanumeric/space/dash chars and uppercased so UK postcodes look right.
      const cleaned = raw.replace(/[^A-Za-z0-9 -]/g, '').slice(0, 10).toUpperCase();
      zip = cleaned.length >= 3 ? cleaned : null;
    }

    if (zip) return { zip, error: null };
    return { zip: null, error: 'Could not determine postcode from your location.' };
  } catch (err) {
    if (import.meta.env.DEV) console.error('[geolocation] geocode error', err);
    return { zip: null, error: 'Could not look up postcode. Please enter it manually.' };
  }
}
